import { useState, useRef, useCallback, useEffect } from "react";
import { columnsHeld } from "@/lib/columnCoordinator";

// Hovering the rail schedules an OPEN after this many ms instead of
// expanding instantly — avoids flicker when the pointer just passes over
// the rail on its way somewhere else. Cancelled on mouseleave before it
// fires, so a quick pass-by never opens the panel.
const OPEN_DELAY = 300;

/**
 * useHoverExpand — drives the "expand on hover, collapse after a delay of no
 * hovering" behaviour shared by the main menu (Sidebar) and every
 * SecondaryColumn. Opening is debounced by OPEN_DELAY (0.3s); the caller
 * renders the expanded surface in-flow (growing the rail's own width) so
 * sibling columns/content shift over rather than being covered.
 *
 * The collapse timer is PAUSED while the pointer is over the panel OR while
 * focus lives inside it (typing in a search box / an open picker) so we never
 * collapse mid-interaction. Clicking anywhere outside the panel while it's
 * open collapses it immediately.
 *
 * Returns:
 *   open      — whether the expanded surface should be shown
 *   ref       — attach to the wrapper element (used for focus-within + outside-click checks)
 *   handlers  — spread onto the wrapper: {onMouseEnter,onMouseLeave,onFocus,onBlur}
 *   close()   — force-collapse now (e.g. after selecting an item)
 */
export default function useHoverExpand({ enabled = true, delay = 2000 } = {}) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const openTimer = useRef(null);
  const hovering = useRef(false);
  // Session 242 — while a column is being resized the pointer routinely
  // leaves the panel; `held` keeps it open for the whole drag.
  const held = useRef(false);
  const ref = useRef(null);

  const clearCloseTimer = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  };
  const clearOpenTimer = () => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
  };

  const focusWithin = () => {
    const el = ref.current;
    const active = typeof document !== "undefined" ? document.activeElement : null;
    return !!(el && active && active !== document.body && el.contains(active));
  };

  const openNow = useCallback(() => {
    if (!enabled) return;
    clearCloseTimer();
    clearOpenTimer();
    setOpen(true);
  }, [enabled]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => {
      // Don't collapse while the pointer is still over the panel, a child
      // holds focus, or a portal popover (e.g. a date picker) is open — a
      // later mouseleave/blur reschedules the close.
      if (held.current || columnsHeld() || hovering.current || focusWithin()) return;
      setOpen(false);
    }, delay);
  }, [delay]);

  // Mouse-in: cancel any pending close, then (if not already open) wait
  // OPEN_DELAY before actually opening.
  const onMouseEnter = useCallback(() => {
    hovering.current = true;
    clearCloseTimer();
    if (!enabled || open) return;
    // A portal popover is open somewhere — don't let a rail the pointer
    // merely crosses on its way there expand and shift the layout.
    if (columnsHeld()) return;
    clearOpenTimer();
    openTimer.current = setTimeout(() => {
      if (hovering.current) setOpen(true);
    }, OPEN_DELAY);
  }, [enabled, open]);

  // Mouse-out: cancel a pending open (so a quick pass-by never expands) and
  // schedule the collapse.
  const onMouseLeave = useCallback(() => {
    hovering.current = false;
    clearOpenTimer();
    scheduleClose();
  }, [scheduleClose]);

  const onFocus = useCallback(() => { openNow(); }, [openNow]);
  const onBlur = useCallback(() => { if (!hovering.current) scheduleClose(); }, [scheduleClose]);

  const close = useCallback(() => {
    clearCloseTimer();
    clearOpenTimer();
    hovering.current = false;
    setOpen(false);
  }, []);

  // Click anywhere outside the panel collapses it right away.
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (held.current || columnsHeld()) return;
      if (ref.current && !ref.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, close]);

  useEffect(() => () => { clearCloseTimer(); clearOpenTimer(); }, []);
  useEffect(() => {
    if (!enabled) { clearCloseTimer(); clearOpenTimer(); hovering.current = false; setOpen(false); }
  }, [enabled]);

  // Hold the panel open (used by the resize drag). Releasing re-arms the
  // normal collapse timer unless the pointer/focus is back inside.
  const setHold = useCallback((next) => {
    held.current = !!next;
    if (next) { clearCloseTimer(); return; }
    if (!hovering.current && !focusWithin()) scheduleClose();
  }, [scheduleClose]);

  return {
    open: enabled && open,
    ref,
    close,
    openNow,
    setHold,
    handlers: { onMouseEnter, onMouseLeave, onFocus, onBlur },
  };
}
